import { useState } from 'react'
import { ShoppingCart, ChevronUp, Loader2, AlertTriangle, X, CheckCircle2, LayoutGrid, ShieldCheck } from 'lucide-react'
import { useIsMobile, useBlockedCartLines } from '@/hooks'
import { calculateCartWeight } from '@/lib/weight'
import { formatCurrency } from '@/lib/utils'
import { cartDiscountDisplayAmount, isRedemptionDiscount, redemptionSavesNothing } from '@/lib/cart'
import { isSaleBlocked } from '@/lib/customerBlock'
import { v2, v2api, type PreviewErrorInfo } from '@/api/v2'
import { useUIStore } from '@/stores/uiStore'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useStoreTaxLabel } from '@/api/stores'
import { OrderDetailModal } from '@/components/orders/OrderDetailModal'
import type { CartItem, Customer } from '@/types'
import { ManagerApprovalDialog } from '@/components/functions/ManagerApprovalDialog'
import {
  approvalRows,
  approveCartItems,
  gateApprovalItems,
  useDiscountLimit,
} from '@/hooks/useDiscountApproval'
import {
  approvalSubtitle,
  approvedByNames,
  approvedByText,
  discountGateBannerText,
  gateDialogInitialError,
  isDiscountGateCode,
  isItemApproved,
  lineApprovalItem,
  linesToRemove,
  refusedFromExtra,
  stripTrailingPeriod,
  limitFromErrorExtra,
  type ApprovalItem,
} from '@/lib/discountLimit'

function parseReceiptId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function parseReceiptNumber(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

// Preview-error copy, keyed off the classified reason (see classifyPreviewError).
// The cart still shows the local estimate and Checkout stays gated in every
// case — only the wording changes, so the cashier isn't told "Reconnecting"
// when the till is plainly online (the field-incident symptom).
function previewErrorBannerText(info: PreviewErrorInfo | null, clerkLimit: number): string {
  // Discount-limit gate: the cashier's two ways out are on the banner.
  const gate = discountGateBannerText(info, clerkLimit)
  if (gate) return gate
  switch (info?.kind) {
    case 'offline':
      return 'No internet connection. Showing local estimate — reconnecting automatically.'
    case 'rejected':
      return info.message
        ? `Couldn't price this cart: ${stripTrailingPeriod(info.message)}. Review the items and try again.`
        : 'Couldn\'t price this cart. Review the items and try again.'
    case 'auth':
      return 'Refreshing your session — showing local estimate.'
    case 'transport':
      return 'Can\'t reach the server. Showing local estimate — retrying automatically.'
    default:
      // 'transient' or unknown: the BE/network blipped; the self-heal loop is
      // already retrying and will re-enable Checkout on its own.
      return 'Could not refresh prices from server. Showing local estimate — retrying automatically.'
  }
}

function previewErrorButtonLabel(info: PreviewErrorInfo | null): string {
  switch (info?.kind) {
    case 'rejected':
      // Retrying the same basket won't help — the cashier must fix the cart.
      return 'Price error'
    case 'offline':
    case 'transport':
      return 'Reconnecting…'
    default:
      return 'Confirming price…'
  }
}

export interface CartSummaryProps {
  cart: CartItem[]
  customer: Customer | null
  onCheckout: () => void
}

export function CartSummary({ cart, customer, onCheckout }: CartSummaryProps) {
  const [showBreakdown, setShowBreakdown] = useState(false)
  const [openOrder, setOpenOrder] = useState<{ receiptId: number; receiptNumber: string } | null>(null)
  const [isOpeningSourceReceipt, setIsOpeningSourceReceipt] = useState(false)
  const { isProcessingAction, showToast, openQuickActions } = useUIStore()
  const isMobile = useIsMobile()
  const { isRefundMode, refundSourceReceipt, serverTotals, isRefundMutating } = usePOSStore()
  const isPaidOnlinePickup = usePOSStore((s) => Boolean(s.loadedEcomMeta?.paidOnline))
  const saleBlocked = isSaleBlocked(customer, { isRefundMode, isPaidOnlinePickup })
  const { label: taxName } = useStoreTaxLabel()

  // v2 totals come from the debounced /cart/preview response held in
  // cartStore. Local cart estimate (computed below) is used for instant
  // display before the first preview returns OR if a preview errors.
  // Lines /cart/preview refused to price. They are missing from `totals`, so
  // without this the summary cheerfully advertises a total that excludes them
  // (the Pickering $0.00 cart: one out-of-stock line, Subtotal/Tax/Total and
  // the Checkout button all $0.00, no explanation). /cart/commit rejects the
  // same cart with 400 cart_contains_invalid_items, so tendering can only fail.
  const { blocked, notices, hasBlocked, hardRejection, byItemId: blockedByItemId } =
    useBlockedCartLines()
  // Same name precedence CartItemRow renders with (getProductFields): the
  // catalog populates `description` far more often than `name`, and reading
  // only `name` left this banner naming nothing at all.
  const blockedNames = blocked
    .map((b) => {
      const product = cart.find((item) => item.id === b.id)?.product
      return product?.display_name || product?.description || product?.name || ''
    })
    .filter((name) => Boolean(name))
    .join(', ')

  const previewTotals = useCartStore((s) => s.preview?.totals ?? null)
  const previewStatus = useCartStore((s) => s.previewStatus)
  const previewError = useCartStore((s) => s.previewError)
  const activeDiscount = useCartStore((s) => s.discount)
  const setDiscount = useCartStore((s) => s.setDiscount)
  // "approved by <name>" under the Discount row(s), from the last priced
  // response (which, for a bound receipt, also lists the approvals already
  // recorded on it). Falls back to the committed receipt's rows when the
  // preview has none. Cashier-side only; the CFD snapshot never carries it.
  const previewOverrides = useCartStore((s) => s.preview?.discount_overrides ?? null)
  const committedOverrides = useCartStore((s) => s.committedOverrides)
  const boundReceiptId = usePOSStore((s) => s.currentReceiptId)
  const discountOverrides = previewOverrides?.length
    ? previewOverrides
    : boundReceiptId != null && committedOverrides?.receiptId === boundReceiptId
      ? committedOverrides.rows
      : previewOverrides
  const receiptApprovedBy = approvedByText(approvedByNames(discountOverrides, 'receipt'))
  // With no cart discount on, every approval is shown under the item line:
  // a recalled receipt's cart discount is replayed as line discounts, so its
  // receipt-scope row approves those lines now.
  const lineApprovedBy = approvedByText(approvedByNames(discountOverrides, 'any'))
  const { limit: clerkLimit } = useDiscountLimit()
  // The values the banner's Approve will approve, fixed when it opens (the
  // dialog lists them; the PIN goes to exactly these). null = closed.
  const [approvalItems, setApprovalItems] = useState<ApprovalItem[] | null>(null)
  const isDiscountGateError = previewStatus === 'error' && isDiscountGateCode(previewError?.code)
  const partiallyPaid = isReceiptPartiallyPaid(serverTotals, isRefundMode)
  // No approval path on a partially-paid receipt: the discount can't change
  // there. Banner Approve and the Checkout-slot Manager Override share this.
  const canApproveGate = isDiscountGateError && !isRefundMode && !partiallyPaid
  const openGateApproval = () => setApprovalItems(gateApprovalItems(previewError?.extra))
  const approvalDialogSubtitle = (() => {
    const { limit, requested } = limitFromErrorExtra(previewError?.extra)
    const lim = limit ?? (clerkLimit < 100 ? clerkLimit : null)
    return lim != null
      ? approvalSubtitle(lim, requested)
      : 'A manager with approval rights enters their PIN.'
  })()

  // Banner "Remove": take the over-limit discount off -- only that one, and
  // say what was cleared. A receipt discount lives on cartStore (and is the
  // gated one whenever it's on: line discounts are off the wire then). Line
  // discounts: the lines the 409 names, else the unapproved ones that
  // measure over the limit against the SERVER-priced line -- never an
  // in-limit one. They go through posStore.updateCartItem (its partial-paid /
  // delivery-line / paid-online gates, line_total recompute and serverTotals
  // reset), and the bridge mirrors them to cartStore.
  const removeGatedDiscounts = () => {
    const extra = previewError?.extra ?? null
    const refused = refusedFromExtra(extra)
    if (activeDiscount && (!refused || refused.scope === 'receipt')) {
      setDiscount(null)
      showToast({ type: 'info', title: 'Cart discount removed' })
      return
    }
    const ps = usePOSStore.getState()
    if (isReceiptPartiallyPaid(ps.serverTotals, ps.isRefundMode)) return
    const { limit } = limitFromErrorExtra(extra)
    const cs = useCartStore.getState()
    const lines = linesToRemove(cs.lines, limit ?? clerkLimit, {
      extra,
      isApproved: (l) => {
        const item = lineApprovalItem(l)
        return item != null && isItemApproved(item, cs.discountApprovals)
      },
    })
    const cleared: string[] = []
    for (const l of lines) {
      // The bridge keys cart lines by product|lot.
      const item = ps.cart.find((i) => i.product_id === l.product_id
        && (i.lot_number ?? null) === (l.lot_number ?? null))
      if (!item) continue
      ps.updateCartItem(item.id, {
        discount_amount: 0,
        discount_percent: 0,
        discount_reason_id: null,
        discount_reason_other: null,
      })
      const p = item.product
      cleared.push(p?.display_name || p?.description || p?.name || `Item #${item.product_id}`)
    }
    showToast(cleared.length
      ? {
          type: 'info',
          title: cleared.length === 1 ? 'Discount removed' : `${cleared.length} discounts removed`,
          message: cleared.join(', '),
        }
      : {
          type: 'warning',
          title: 'No discount removed',
          message: 'Couldn\'t tell which discount is over the limit. Open the item and change its discount.',
        })
  }

  // Calculate cart weight from product size field
  const cartWeight = calculateCartWeight(cart)

  // Get customer limits
  const purchaseLimit = customer?.purchase_limit ?? 30
  const currentPurchase = customer?.daily_purchased_grams ?? 0

  // Total daily usage = already purchased today + current cart
  const totalDailyUsage = currentPurchase + cartWeight
  const remainingLimit = purchaseLimit - totalDailyUsage
  const isOverLimit = remainingLimit < 0
  const isNearLimit = remainingLimit >= 0 && remainingLimit < 5

  // Sale savings (display only — actual discount applied server-side)
  const saleDiscount = cart.reduce((sum, item) => {
    if (item.product.on_sale && item.product.compare_price) {
      const origPrice = typeof item.product.compare_price === 'number'
        ? item.product.compare_price
        : parseFloat(String(item.product.compare_price)) || 0
      const price = typeof item.unit_price === 'number' ? item.unit_price : parseFloat(String(item.unit_price)) || 0
      return sum + (origPrice - price) * item.quantity
    }
    return sum
  }, 0)

  const estimated = cart.reduce((acc, item) => {
    const qty = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0
    const sign = qty < 0 ? -1 : 1
    const absQty = Math.abs(qty)
    const lineGross = typeof item.line_total === 'number' ? item.line_total : parseFloat(String(item.line_total)) || 0

    const lineSubtotal = lineGross
    const lineTax = 0

    const depositUnit = Number(item.product.deposit_price ?? item.product.recycling_fee ?? 0) || 0
    const lineDeposit = depositUnit * absQty * sign

    acc.subtotal += lineSubtotal
    acc.tax += lineTax
    acc.deposit += lineDeposit
    return acc
  }, { subtotal: 0, tax: 0, deposit: 0 })

  // Tax/total are computed in three tiers, in order of fidelity:
  //
  //   1. previewStatus === 'idle' AND preview is current
  //      => use preview totals verbatim (BE-priced, confirmed).
  //   2. preview exists but is stale (cart changed and preview is in flight,
  //      OR the last refresh errored — see the `isStale` note below)
  //      => use the LOCAL subtotal (which adjusts instantly with the cart)
  //         and extrapolate tax via the previous preview's tax/subtotal
  //         ratio. So adding a $10 line to a cart where the BE last said
  //         "13% effective tax rate" instantly shows ~$1.30 of new tax.
  //         This matches the eventual BE compute almost exactly because
  //         tax-mode (inclusive vs exclusive), exemptions, and category
  //         rates all stay constant between mutations.
  //   3. no preview yet (first render before /cart/preview returns)
  //      => show subtotal only with a "calculating tax..." pill. Tax shows
  //         as $0 and the cashier sees the indicator. First preview
  //         resolves it in ~250ms.
  //
  // Subtotal always tracks the current cart locally so it feels instant.
  //
  // 'error' is bundled with 'stale'/'fetching' on purpose: when the last
  // /cart/preview FAILED we must NOT fall into Tier 1 and render the
  // previous preview's server totals as "confirmed". That stale preview can
  // be a basket-shape behind the cart — e.g. the cashier bumped qty 1→2 and
  // the refresh preview then errored — so its subtotal/tax/total describe
  // the OLD basket (qty 1). Tier 1 would show that old number with a green
  // "confirmed" check while every CartItemRow already shows the new-qty line
  // total, so the Subtotal visibly disagrees with the line items and the
  // Checkout button advertises the stale, lower total. Treating 'error' as
  // stale routes it to the local-estimate tiers (Tier 2 when a prior preview
  // exists, Tier 3 otherwise) — which is what the file-header comment and the
  // "Showing local estimate" banner already promise. (When the cart shape
  // hasn't changed since the last good preview, Tier 2's local subtotal
  // equals that preview's subtotal, so this is a no-op for the plain
  // refresh-then-fail case and only corrects the shape-changed-then-fail case.)
  const isStale =
    previewStatus === 'stale'
    || previewStatus === 'fetching'
    || previewStatus === 'error'

  const localSubtotal = estimated.subtotal
  const localDeposit = estimated.deposit

  let subtotal: number
  let tax: number
  let total: number
  let displayMode: 'confirmed' | 'extrapolated' | 'estimating'
  if (serverTotals !== null) {
    // Tier 0 -- BE has already confirmed totals via a non-preview path
    // (refund via /receipts/copy-with-refund, unhold response, etc.).
    // /cart/preview is gated off in refund mode (negative qty would make
    // the BE reject the request), so without this branch the refund
    // summary falls through to Tier 3 estimating: subtotal sums the
    // tax-inclusive line_total values, tax is 0, and the spinner never
    // resolves. ServerTotals carries net subtotal + tax + deposit + total
    // pre-signed (negative for refunds), so they slot in verbatim.
    subtotal = serverTotals.subtotal
    tax = serverTotals.taxes
    total = serverTotals.total
    displayMode = 'confirmed'
  } else if (previewTotals !== null && !isStale) {
    // Tier 1 -- BE-confirmed, exact match. Use server subtotal too so a
    // customer-tier reprice (e.g. $86.92 catalog → $70.47 tier) is
    // reflected here. Previously this line was pinned to localSubtotal
    // for "instant feel," but that meant Tax + Total displayed the
    // tier-correct numbers while Subtotal still summed the catalog
    // line_totals — the three didn't add up and the cashier saw a
    // visibly wrong "Subtotal: $189.22, Tax: $7.82, Total: $164.14."
    subtotal = parseFloat(previewTotals.subtotal) || 0
    tax = parseFloat(previewTotals.taxes) || 0
    total = parseFloat(previewTotals.total) || 0
    displayMode = 'confirmed'
  } else if (previewTotals !== null) {
    // Tier 2 -- last preview's effective tax rate applied to current local
    // subtotal. Survives between mutations until next preview returns.
    // Local subtotal is what feels instant when the cashier mutates the
    // cart between preview cycles, so use it here. Once Tier 1 kicks
    // back in (preview re-confirms), we snap to the server's number.
    const prevSub = parseFloat(previewTotals.subtotal) || 0
    const prevTax = parseFloat(previewTotals.taxes) || 0
    const ratio = prevSub > 0 ? prevTax / prevSub : 0
    subtotal = localSubtotal
    tax = +(localSubtotal * ratio).toFixed(2)
    total = +(localSubtotal + tax + localDeposit).toFixed(2)
    displayMode = 'extrapolated'
  } else {
    // Tier 3 -- first preview hasn't returned yet.
    subtotal = localSubtotal
    tax = 0
    total = localSubtotal + localDeposit
    displayMode = 'estimating'
  }
  const deposit = localDeposit

  // Total items count
  // Counts only what the Subtotal beside it actually includes. A blocked line
  // contributes no money, so counting it here reproduced the contradiction
  // this whole change exists to kill ("Subtotal (2 items) $21.99" on a cart
  // whose second item was refused).
  const itemCount = cart.reduce(
    (sum, item) => (blockedByItemId.has(item.id) ? sum : sum + Math.abs(item.quantity)),
    0,
  )

  // Refund source pointer:
  // - Preferred: explicit refund linkage on the refund receipt (`src_*` /
  //   `source_*`) that points back to the original sale.
  // - Fallback: older flows stash a minimal `{id, number}` source object.
  const sourceReceiptId =
    parseReceiptId(refundSourceReceipt?.src_id)
    ?? parseReceiptId(refundSourceReceipt?.source_id)
    ?? (refundSourceReceipt?.is_return === true ? null : parseReceiptId(refundSourceReceipt?.id))
  const sourceReceiptNumber =
    parseReceiptNumber(refundSourceReceipt?.src_number)
    ?? parseReceiptNumber(refundSourceReceipt?.source_number)
    ?? (refundSourceReceipt?.is_return === true ? null : parseReceiptNumber(refundSourceReceipt?.number))
  const canOpenSourceReceipt = sourceReceiptId != null || sourceReceiptNumber != null

  const handleOpenSourceReceipt = async () => {
    if (!refundSourceReceipt || isOpeningSourceReceipt) return

    if (sourceReceiptId != null) {
      setOpenOrder({
        receiptId: sourceReceiptId,
        receiptNumber: sourceReceiptNumber ?? String(sourceReceiptId),
      })
      return
    }

    if (!sourceReceiptNumber) {
      showToast({
        type: 'error',
        title: 'Source receipt unavailable',
        message: 'This refund is missing a source receipt link.',
      })
      return
    }

    setIsOpeningSourceReceipt(true)
    try {
      const data = await v2api.get<unknown>(
        v2('/receipts/search'),
        { q: sourceReceiptNumber, any: true, limit: 5 },
      )
      const list = Array.isArray(data)
        ? (data as Array<Record<string, unknown>>)
        : ((data as { receipts?: Array<Record<string, unknown>> })?.receipts ?? [])
      const match = list.find(
        (r) => typeof r.number === 'string' && r.number === sourceReceiptNumber,
      )
      const resolvedId = match && typeof match.id === 'number' ? match.id : null
      if (resolvedId == null) {
        showToast({
          type: 'error',
          title: 'Source receipt not found',
          message: `Could not locate ${sourceReceiptNumber}.`,
        })
        return
      }
      setOpenOrder({
        receiptId: resolvedId,
        receiptNumber: sourceReceiptNumber,
      })
    } catch {
      showToast({
        type: 'error',
        title: 'Failed to open source',
        message: 'Try opening the receipt from the orders list.',
      })
    } finally {
      setIsOpeningSourceReceipt(false)
    }
  }

  return (
    <div className="border-t border-gray-200 flex-shrink-0">
      {/* Preview-error banner: /cart/preview failed but the cart UI keeps
          working off the local estimate. Checkout is still allowed -- the
          BE will recompute on commit and reject if anything's off. */}
      {previewStatus === 'error' && cart.length > 0 && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-600 shrink-0" />
          <span className="text-xs text-amber-800 leading-tight flex-1">
            {previewErrorBannerText(previewError, clerkLimit)}
          </span>
          {/* Discount-limit gate: Approve / Remove. */}
          {canApproveGate && (
            <span className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={openGateApproval}
                className="px-2 py-1 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 cursor-pointer"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={removeGatedDiscounts}
                className="px-2 py-1 text-xs font-medium rounded-md border border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer"
              >
                Remove
              </button>
            </span>
          )}
        </div>
      )}
      <ManagerApprovalDialog
        isOpen={approvalItems !== null}
        onClose={() => setApprovalItems(null)}
        title="Approve discount"
        subtitle={approvalDialogSubtitle}
        initialError={gateDialogInitialError(previewError)}
        items={approvalRows(approvalItems ?? [])}
        onSubmit={(pin) => approveCartItems(approvalItems ?? [], pin)}
      />

      {/* Blocked-line banner. The rows themselves are already red with the
          reason (CartItemRow), so this is the cart-level summary + the
          instruction that actually unsticks the cashier: remove them. */}
      {hardRejection && (
        <div className="px-4 py-2.5 bg-red-50 border-b border-red-200 flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-red-800 leading-tight">
              {!hasBlocked
                ? 'This sale can’t be completed as it stands'
                : blocked.length === 1
                  ? '1 item can’t be sold and is not in the total'
                  : `${blocked.length} items can’t be sold and are not in the total`}
            </p>
            <p className="text-xs text-red-700 leading-tight mt-0.5">
              {hasBlocked
                ? `${blockedNames || 'See the highlighted lines above.'} — remove to continue.`
                : notices.join(' ')}
            </p>
          </div>
        </div>
      )}

      {/* Preview notices that belong to no single line (e.g. cannabis limit). */}
      {notices.length > 0 && !hardRejection && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
          <span className="text-xs text-amber-800 leading-tight">{notices.join(' ')}</span>
        </div>
      )}

      {/* Refund Mode Banner */}
      {isRefundMode && refundSourceReceipt && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200">
          <div className="text-sm text-red-800 font-medium">
            {canOpenSourceReceipt ? (
              <button
                type="button"
                onClick={handleOpenSourceReceipt}
                disabled={isOpeningSourceReceipt}
                className="inline-flex items-center gap-1 text-left text-red-800 hover:underline cursor-pointer disabled:no-underline disabled:opacity-80"
                title="Open source receipt"
              >
                <span>Refunding #{refundSourceReceipt.number}</span>
                {isOpeningSourceReceipt && <Loader2 size={12} className="animate-spin" />}
              </button>
            ) : (
              <span>Refunding #{refundSourceReceipt.number}</span>
            )}
          </div>
          <div className="text-xs text-red-600 mt-1">
            Adjust quantities or remove items as needed
          </div>
        </div>
      )}

      {/* Purchase Limit Tracker - hide in refund mode */}
      {!isRefundMode && (
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-500">
              Limit{' '}
              <span className={isOverLimit ? 'text-red-500' : isNearLimit ? 'text-amber-500' : 'text-gray-500'}>
                ({isOverLimit ? `${Math.abs(remainingLimit).toFixed(1)}g over` : `${remainingLimit.toFixed(1)}g left`})
              </span>
            </span>
            <span
              className={`font-medium ${isOverLimit ? 'text-red-500' : isNearLimit ? 'text-amber-500' : 'text-gray-700'}`}
            >
              {totalDailyUsage.toFixed(1)} / {purchaseLimit}g
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            {/* Show purchased today as darker segment */}
            <div className="h-2 flex">
              {currentPurchase > 0 && (
                <div
                  className="h-2 bg-gray-400"
                  style={{
                    width: `${Math.min((currentPurchase / purchaseLimit) * 100, 100)}%`,
                  }}
                />
              )}
              <div
                className={`h-2 transition-all ${
                  isOverLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-green-500'
                }`}
                style={{
                  width: `${Math.min((cartWeight / purchaseLimit) * 100, 100 - (currentPurchase / purchaseLimit) * 100)}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sale savings breakdown (collapsible) */}
      {saleDiscount > 0 && (
        <div
          className="overflow-hidden transition-[max-height] duration-200 ease-out"
          style={{ maxHeight: showBreakdown ? '80px' : '0px' }}
        >
          <div className="px-4 py-2 space-y-1 text-sm border-t border-gray-100">
            <div className="flex justify-between text-green-600">
              <span>Sale Savings</span>
              <span>-{formatCurrency(saleDiscount)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary - always visible. Numbers come from /cart/preview when
          available, local estimate otherwise -- never shows "..." so the
          cart feels instant. The "previewing" pill below the total
          communicates pending vs confirmed. */}
      <div className="px-4 pt-3 pb-2">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal ({itemCount} items)</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {activeDiscount && (
            <div className="flex justify-between items-center">
              <span className="text-gray-500 flex items-center gap-1.5">
                {isRedemptionDiscount(activeDiscount) ? 'Loyalty' : 'Discount'}
                <span className="text-xs text-gray-400">
                  {activeDiscount.percent != null
                    ? `(${activeDiscount.percent}%)`
                    : activeDiscount.amount != null
                      ? `(${formatCurrency(activeDiscount.amount)})`
                      : activeDiscount.coupon
                        ? `(${activeDiscount.coupon})`
                        : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setDiscount(null)}
                  className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                  aria-label="Remove discount"
                  title="Remove discount"
                >
                  <X size={12} />
                </button>
              </span>
              {/* A settled $0 redemption is still spent at commit; flag it
                  here too, since the checkout banner is the only other
                  place that says so. */}
              {/* With a cart discount on, it's the gated one (line
                  discounts are off the wire then): it isn't pending, it's
                  waiting on a manager. */}
              {isDiscountGateError ? (
                <span className="text-amber-700 text-xs font-medium">needs approval</span>
              ) : previewTotals && !isStale && redemptionSavesNothing(previewTotals, activeDiscount) ? (
                <span className="text-amber-700 flex items-center gap-1 text-xs">
                  <AlertTriangle size={12} aria-hidden="true" />
                  saves nothing, still used up
                </span>
              ) : (
                <span className="text-green-600">
                  {previewTotals && !isStale
                    ? `-${formatCurrency(cartDiscountDisplayAmount(previewTotals, activeDiscount))}`
                    : 'pending…'}
                </span>
              )}
            </div>
          )}
          {activeDiscount && receiptApprovedBy && (
            <div className="text-xs text-gray-400 -mt-0.5">{receiptApprovedBy}</div>
          )}
          {!activeDiscount && lineApprovedBy && (
            <div className="text-xs text-gray-400">Item discount {lineApprovedBy}</div>
          )}
          {deposit > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Deposit</span>
              <span>{formatCurrency(deposit)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">Tax ({taxName})</span>
            <span>{formatCurrency(tax)}</span>
          </div>
        </div>

        {/* Total (clickable to toggle breakdown). Status icon sits
            inline next to the label: confirmed = green check,
            extrapolated = amber spinner, estimating = gray spinner,
            error = small "Local estimate" text. Icon-only states keep
            the row compact and avoid stealing visual weight from the
            total amount. */}
        <div
          className="mt-3 pt-3 border-t border-gray-100 cursor-pointer select-none"
          onClick={() => setShowBreakdown((prev) => !prev)}
        >
          <div className="flex justify-between items-center text-lg font-bold">
            <span className="flex items-center gap-1.5">
              {isRefundMode ? 'Refund Total' : 'Total'}
              {/* Refund-mode item edits round-trip to the BE (PUT/DELETE
                  on receipt items) and re-seed serverTotals from the
                  response. While that round trip is in flight the total
                  shown is stale, so we override the normal status icon
                  with a spinner so the cashier sees the lag.  */}
              {cart.length > 0 && isRefundMode && isRefundMutating ? (
                <Loader2
                  size={14}
                  className="text-amber-500 animate-spin"
                  aria-label="Updating refund total"
                >
                  <title>Updating refund total</title>
                </Loader2>
              ) : /* Skip the status indicator entirely on an empty cart —
                     there's nothing being priced, so the spinner-on-zero
                     state is just visual noise. */
              cart.length > 0 && displayMode === 'confirmed' ? (
                <CheckCircle2
                  size={16}
                  className="text-green-600"
                  aria-label="Confirmed by server"
                >
                  <title>Confirmed by server</title>
                </CheckCircle2>
              ) : cart.length > 0 && displayMode === 'extrapolated' ? (
                <Loader2
                  size={14}
                  className="text-amber-500 animate-spin"
                  aria-label="Updating prices"
                >
                  <title>Updating prices</title>
                </Loader2>
              ) : cart.length > 0 && displayMode === 'estimating' ? (
                <Loader2
                  size={14}
                  className="text-gray-400 animate-spin"
                  aria-label="Calculating tax"
                >
                  <title>Calculating tax</title>
                </Loader2>
              ) : null}
              {saleDiscount > 0 && (
                <ChevronUp
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 ${showBreakdown ? '' : 'rotate-180'}`}
                />
              )}
            </span>
            <span
              className={`${isRefundMode ? 'text-red-600' : isDiscountGateError ? 'text-gray-400' : 'text-green-600'} ${
                isRefundMode && isRefundMutating ? 'opacity-50' : ''
              }`}
            >
              {/* Render the signed total verbatim — refund totals are
                  already negative coming back from /cart/preview, sale
                  totals positive. Stripping the sign here while the
                  Checkout button below shows the signed value (or vice
                  versa) is what creates the "$33 vs -$33" cashier
                  confusion in edge states. One rule: signed both
                  places. */}
              {formatCurrency(total)}
            </span>
          </div>
          {/* Error case still gets a sub-line — it's the only state
              where the cashier needs to know why pricing might be off
              (BE preview failed, falling back to local math). */}
          {previewStatus === 'error' && (
            <div className="text-xs mt-1 text-gray-400">
              {/* The over-limit discount isn't priced in, so don't let this
                  read as the amount to charge. */}
              {isDiscountGateError ? 'Not final — discount needs manager approval' : 'Local estimate'}
            </div>
          )}
        </div>

      </div>

      {/* (V2PreviewReadout component defined below renders inside the summary) */}

      {/* Checkout Button. Block while /cart/preview is in flight OR has
          errored in sale mode — in both states the displayed total isn't a
          server-confirmed price for the CURRENT basket, so letting the
          cashier tender risks charging a stale amount (the line rows show
          the live qty while the totals lag a shape behind) and a downstream
          price_drift rejection. The 'error' block is paired with a self-heal
          retry in useV2PreviewBridge: while errored, the bridge re-attempts
          the preview on a fixed cadence, so this button re-enables on its own
          the moment the backend recovers — the cashier never has to poke a
          line to unstick it. Refund mode never fires preview (negative qty
          rejected), so the gate is sale-only. Refund mode
          gates on isRefundMutating instead: while a PUT/DELETE on the
          persisted refund receipt is in flight, the total shown here
          hasn't been re-seeded from the BE response yet, and Process
          Refund would commit the old amount.

          Partial-payment label: when serverTotals reports a remaining
          balance < total, the button advertises what the customer will
          actually be charged on THIS swipe ("Continue Payment $25.41 of
          $54.16") rather than the receipt gross. The previous wording
          ("Checkout $54.16") on a half-paid cart misled cashiers into
          believing nothing had been collected -- the trigger for the
          Ingersoll 2026-05-26 standalone-Moneris incident. The banner
          in RightSidebar carries the same info; the button label is the
          last line of defense at the moment of tender. */}
      <div className="px-4 pb-4 flex items-stretch gap-2" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        {(() => {
          // Pending = preview not yet settled for the CURRENT basket:
          // 'stale' the instant the cart changes, then 'fetching' during the
          // debounced call. In both the shown total is a local estimate, so
          // checkout stays blocked until it confirms ('idle') — matching the
          // handleCheckout source gate so the button never looks tappable
          // while the handler would reject it. Both gates exempt the
          // serverTotals case (partial-payment continuation / committed
          // receipt): there the BE-authoritative serverTotals drives the
          // tender, not the preview, so previewStatus is irrelevant.
          const isPreviewPending =
            !isRefundMode
            && serverTotals === null
            && (previewStatus === 'stale' || previewStatus === 'fetching')
          // Preview failed: the cart is on a local estimate (or a
          // shape-behind server total), never confirmed for the current
          // basket. Block tender until the bridge's self-heal retry snaps
          // previewStatus back to 'idle'. Sale-only — refund mode doesn't
          // preview.
          const isPreviewError =
            !isRefundMode && serverTotals === null && previewStatus === 'error'
          const isRefundPending = isRefundMode && isRefundMutating
          // Online-PAID pickup view: the order is charged and immutable, so
          // none of the pricing gates (preview confirmation, daily limit)
          // apply — the button is a single "Complete" (pickup) action.
          if (isPaidOnlinePickup) {
            return (
              <button
                onClick={onCheckout}
                disabled={cart.length === 0 || isProcessingAction}
                className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer text-white py-3.5 md:py-3 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2"
              >
                {isProcessingAction
                  ? <Loader2 size={20} className="animate-spin" />
                  : <ShoppingCart size={20} />
                }
                {isProcessingAction
                  ? 'Processing...'
                  : `Complete ${formatCurrency(total)} (Paid Online)`}
              </button>
            )
          }
          // Over-limit discount: the cart can't be priced until a manager
          // approves it, so the Checkout slot opens the approval instead of
          // sitting disabled as "Price error". The daily limit and refused
          // lines still win -- an approval fixes neither.
          if (canApproveGate && !isOverLimit && !hardRejection && !saleBlocked && cart.length > 0) {
            return (
              <button
                type="button"
                onClick={openGateApproval}
                disabled={isProcessingAction}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer text-white py-3.5 md:py-3 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2"
              >
                <ShieldCheck size={20} />
                Manager Override
              </button>
            )
          }
          const disabled =
            cart.length === 0
            || (!isRefundMode && isOverLimit)
            // A cart holding a line the BE refused can only 400 on commit, and
            // the total on this button excludes that line's money. Gate on
            // hardRejection rather than hasBlocked: commit fails on ANY ops_error,
            // including one we could not pin to a visible row (a basket's child
            // sku), where hasBlocked is false but the tender is still doomed.
            // The hook returns nothing in refund / serverTotals carts.
            || hardRejection
            || saleBlocked
            || isProcessingAction
            || isPreviewPending
            || isPreviewError
            || isRefundPending
          const isPartiallyPaid = isReceiptPartiallyPaid(serverTotals, isRefundMode)
          const remainingAmount = isPartiallyPaid && serverTotals
            ? serverTotals.amountToPay
            : 0
          return (
            <button
              onClick={onCheckout}
              disabled={disabled}
              className={`flex-1 ${
                isRefundMode
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
              } disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer text-white py-3.5 md:py-3 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center gap-2`}
            >
              {isProcessingAction || isPreviewPending || isPreviewError || isRefundPending
                ? <Loader2 size={20} className="animate-spin" />
                : <ShoppingCart size={20} />
              }
              {saleBlocked
                ? 'Customer Blocked'
                : isOverLimit && !isRefundMode
                ? 'Over Daily Limit'
                : hardRejection
                ? hasBlocked
                  ? blocked.length === 1 ? 'Remove Unavailable Item' : 'Remove Unavailable Items'
                  : 'Cart Has an Unavailable Item'
                : isProcessingAction
                  ? 'Processing...'
                  : isPreviewPending
                    ? 'Calculating…'
                    : isPreviewError
                      ? previewErrorButtonLabel(previewError)
                      : isRefundPending
                        ? 'Updating refund…'
                      : isRefundMode
                        ? `Process Refund ${formatCurrency(total)}`
                        : isPartiallyPaid
                          ? `Checkout Remaining ${formatCurrency(remainingAmount)}`
                          : `Checkout ${formatCurrency(total)}`
              }
            </button>
          )
        })()}
        {/* Quick-actions launcher (the "20%") on the cart view's Checkout
            bar — mobile only; desktop keeps the full-width button. */}
        {isMobile && (
          <button
            type="button"
            onClick={openQuickActions}
            aria-label="Quick actions"
            className="flex-shrink-0 px-5 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors cursor-pointer"
          >
            <LayoutGrid size={22} />
          </button>
        )}
      </div>

      {openOrder && (
        <OrderDetailModal
          receiptId={openOrder.receiptId}
          receiptNumber={openOrder.receiptNumber}
          hideAddActions
          onClose={() => setOpenOrder(null)}
        />
      )}
    </div>
  )
}


